/** Format a YYYY-MM-DD string to a human-readable date like "Sun, May 21, 2026". */
export function formatDate(d: string): string {
  try {
    // Parse as UTC noon to avoid DST-driven day-shift.
    const dt = new Date(`${d}T12:00:00Z`);
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(dt);
  } catch {
    return d;
  }
}

/**
 * Format an ISO-8601 datetime as a date-only string in UTC (e.g. "Aug 1,
 * 2026"). For date-only concepts stored as a UTC-midnight timestamp (e.g. a
 * grant expiry): rendering with formatDateTime would show a spurious time and
 * could shift the day for viewers west of UTC. This pins the calendar day to
 * UTC so the displayed date matches what was entered.
 */
export function formatDateUTC(dt: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(dt));
  } catch {
    return dt;
  }
}

/** Format an ISO-8601 datetime string (UTC) to a human-readable datetime. */
export function formatDateTime(dt: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(dt));
  } catch {
    return dt;
  }
}

/** Format an ISO-8601 datetime string relative to now. */
export function relativeDateTime(d: string): string {
  try {
    const date = new Date(d);
    const now = new Date();
    const diff = (now.getTime() - date.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    if (diff < 172800) return 'Yesterday';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return d;
  }
}

/**
 * Format a check-in / check-out date pair as a compact range.
 * Same month: "Aug 13 – 15"; cross-month: "Jul 28 – Aug 2".
 * Returns "" when either date is null/undefined.
 */
export function fmtRange(
  checkin: string | undefined | null,
  checkout: string | undefined | null,
): string {
  if (!checkin || !checkout) return '';
  try {
    const ci = new Date(`${checkin}T12:00:00Z`);
    const co = new Date(`${checkout}T12:00:00Z`);
    const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', ...opts }).format(d);
    const ciStr = fmt(ci, { month: 'short', day: 'numeric' });
    if (ci.getUTCMonth() === co.getUTCMonth() && ci.getUTCFullYear() === co.getUTCFullYear()) {
      return `${ciStr} – ${fmt(co, { day: 'numeric' })}`;
    }
    return `${ciStr} – ${fmt(co, { month: 'short', day: 'numeric' })}`;
  } catch {
    return '';
  }
}

/** Format a minor-unit amount (e.g. cents) as a currency string. */
export function formatMoney(minorAmount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(minorAmount / 100);
  } catch {
    return `${(minorAmount / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * Compact currency for tight cells (no decimals): 12000, "EUR" → "€120".
 * Used in the calendar grid where a 64px cell can't fit "€120.00".
 */
export function formatMoneyShort(minorAmount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(minorAmount / 100);
  } catch {
    return `${Math.round(minorAmount / 100)} ${currency}`;
  }
}

/**
 * Compact nightly rate for the calendar grid, FLOORED to whole units to match
 * how Channex displays the rate in its own calendar (it truncates the
 * fractional part — a €73.50 rate shows as "€73", not "€74"). Using this instead
 * of formatMoneyShort (which rounds half-up) keeps our calendar and Channex's in
 * visual agreement. Display only — the real fractional rate that we store and
 * push to Channex is unchanged.
 */
export function formatRateShort(minorAmount: number, currency: string): string {
  const flooredMajor = Math.floor(minorAmount / 100);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(flooredMajor);
  } catch {
    return `${flooredMajor} ${currency}`;
  }
}
